import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { cn } from '../utils/cn';
import { X } from 'lucide-react';
import { useFocusTrap } from './useFocusTrap';
import { Button } from './button';

export interface ModalProps {
  visible?: boolean;
  title?: React.ReactNode;
  closable?: boolean;
  keyboard?: boolean;
  maskClosable?: boolean;
  centered?: boolean;
  destroyOnClose?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
  footer?: React.ReactNode | null;
  width?: number | string;
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  className?: string;
  wrapClassName?: string;
  maskStyle?: React.CSSProperties;
  okText?: string;
  cancelText?: string;
  children?: React.ReactNode;
}

export function Modal({
  visible = false,
  title,
  closable = true,
  keyboard = true,
  maskClosable = true,
  centered: _centered = false,
  destroyOnClose = false,
  onOk,
  onCancel,
  footer,
  width,
  style,
  bodyStyle,
  className,
  wrapClassName,
  maskStyle,
  okText = 'Ok',
  cancelText = 'Avbryt',
  children,
}: ModalProps) {
  const [mounted, setMounted] = React.useState(visible);
  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (visible) {
      setMounted(true);
    } else if (destroyOnClose) {
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [visible, destroyOnClose]);

  React.useEffect(() => {
    if (!visible || !keyboard) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, keyboard, onCancel]);

  useFocusTrap(modalRef, visible);

  React.useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [visible]);

  if (!mounted && destroyOnClose) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (maskClosable && e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  const showDefaultFooter = footer !== null && footer === undefined;

  const scrollbarHideStyle = `
    .modal-body-scroll::-webkit-scrollbar { display: none; }
    .modal-body-scroll { -ms-overflow-style: none; scrollbar-width: none; }
  `;

  return ReactDOM.createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[1000]',
        'transition-opacity duration-normal ease-smooth',
        visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        wrapClassName
      )}
      aria-modal="true"
      role="dialog"
    >
      <style>{scrollbarHideStyle}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/45"
        style={maskStyle}
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        className="fixed inset-0 flex items-start justify-center pointer-events-none p-2 pt-2.5"
      >
        <div
          ref={modalRef}
          className={cn(
            'relative bg-white rounded-sm shadow-lg pointer-events-auto',
            'w-full max-w-lg flex flex-col',
            'transition-transform duration-normal ease-smooth',
            visible ? 'translate-y-0' : '-translate-y-3',
            className
          )}
          style={{
            width,
            maxHeight: 'calc(100vh - 20px)',
            ...style
          }}
        >
          {/* Close button */}
          {closable && (
            <button
              onClick={onCancel}
              className={cn(
                'absolute top-0 right-0 z-50 w-14 h-14',
                'flex items-center justify-center',
                'border-0! bg-transparent! tracking-normal! normal-case!',
                'text-muted hover:text-foreground transition-colors duration-fast'
              )}
              aria-label="Stäng"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          )}

          {/* Title */}
          {title && (
            <div className="px-6 py-4 border-b border-border shrink-0">
              <h3 className="m-0 text-base font-medium text-foreground leading-snug">{title}</h3>
            </div>
          )}

          {/* Body */}
          <div
            className="modal-body-scroll overflow-auto flex-1 min-h-0"
            style={bodyStyle}
          >
            {children}
          </div>

          {/* Footer */}
          {footer !== null && (
            <div className="px-4 py-2.5 border-t border-border text-right shrink-0">
              {footer !== undefined ? (
                footer
              ) : showDefaultFooter ? (
                <div className="flex justify-end gap-2">
                  {cancelText && (
                    <Button variant="default" size="small" onClick={onCancel}>
                      {cancelText}
                    </Button>
                  )}
                  {okText && (
                    <Button variant="primary" size="small" onClick={onOk}>
                      {okText}
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
